import { TFunction, Trans, useTranslation } from 'react-i18next';
import { AppDispatch } from 'redux/store';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, {
	CollaboratorPermission,
	InviteCollaboratorCommand,
	ProjectCollaboratorDto,
	UpdateCollaboratorsCommand,
} from 'api';
import {
	Button,
	Checkbox,
	Modal,
	Select,
	Space,
	Table,
	TablePaginationConfig,
	Layout,
	Col,
} from 'antd';
import { getCellValue, getShowTotal } from 'utils/tableUtils';
import {
	deleteCollaborator,
	getCollaborators,
	updateCollaborator,
} from 'redux/reducers/collaboratorSlice';
import { useParams } from 'react-router-dom';
import styles from './CollaboratorsPage.module.less';
import {
	getAllCollaboratorPermissions,
	getCollaboratorPermissions,
} from 'utils/productUtils';
import {
	DeleteOutlined,
	EditOutlined,
	MailOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import OmniNotification from 'components/OmniNotification';
import InviteModal from './InviteModal';
import { ArgsProps } from 'antd/lib/notification';

type ParamsProps = {
	projectId: string;
};

type HandleChangeParams = {
	collaboratorId: number | undefined;
	collaboratorName?: string;
	permissions?: CollaboratorPermission[];
};

const getColumns = (
	t: TFunction,
	deleteModalOpen: Function,
	editModalOpen: Function,
	resendEmail: Function,
) => [
	{
		title: t('collaboratorsPage.collaboratorsTable.userEmail'),
		key: 'email',
		render: (record: ProjectCollaboratorDto) => {
			if (record.userName) {
				return getCellValue(record.userEmail);
			} else {
				return (
					<div>
						<span style={{ float: 'left' }}>
							{getCellValue(record.userEmail)}
						</span>
						<Col style={{ float: 'left' }} sm={24}>
							<span
								className={styles.invitationPending}
								data-testid="invitationPendingLabel"
							>
								{t(
									'collaboratorsPage.collaboratorsTable.email.pendingInvitation',
								)}
							</span>

							<Button
								type="link"
								className={styles.resendBtn}
								data-testid="resendBtn"
								onClick={() => resendEmail(record)}
							>
								<MailOutlined />{' '}
								{t('collaboratorsPage.collaboratorsTable.email.resend')}
							</Button>
						</Col>
					</div>
				);
			}
		},
	},
	{
		title: t('collaboratorsPage.collaboratorsTable.userName'),
		key: 'name',
		width: 300,
		render: (record: ProjectCollaboratorDto) => getCellValue(record.userName),
	},
	{
		title: t('collaboratorsPage.collaboratorsTable.permissions'),
		key: 'Permissions',
		render: (record: ProjectCollaboratorDto) => {
			if (record.isProjectAdministrator) {
				return t('collaboratorsPage.collaboratorsTable.permissions.All');
			} else if (record.permissions) {
				let permissions: string[] = [];
				getCollaboratorPermissions(t, record.permissions).map((permission) =>
					permissions.push(t(permission.name)),
				);
				return permissions.join(', ');
			} else {
				return getCellValue(record.permissions);
			}
		},
	},
	{
		title: t('collaboratorsPage.collaboratorsTable.edit'),
		key: 'edit',
		render: (record: ProjectCollaboratorDto) => (
			<EditOutlined
				data-testid="editBtn"
				onClick={() => editModalOpen(record)}
			/>
		),
		width: 80,
		align: 'center' as const,
	},
	{
		title: t('collaboratorsPage.collaboratorsTable.delete'),
		key: 'delete',
		render: (record: ProjectCollaboratorDto) => (
			<DeleteOutlined
				onClick={() =>
					deleteModalOpen(
						record.collaboratorId ?? 0,
						record.userName || record.userEmail,
					)
				}
			/>
		),
		width: 80,
		align: 'center' as const,
	},
];

const { Option } = Select;
export default function CollaboratorsPage() {
	const { t } = useTranslation();
	const dispatch: AppDispatch = useDispatch();
	const [projectCollaborators, setProjectCollaborators] =
		useState<ProjectCollaboratorDto[]>();
	const [isLoading, setIsLoading] = useState(false);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const projectId = parseInt(useParams<ParamsProps>().projectId);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editModalContent, setEditModalContent] =
		useState<ProjectCollaboratorDto>();
	const editModalOpen = useCallback(
		(record: ProjectCollaboratorDto) => {
			setEditModalVisible(true);
			setEditModalContent(record);
			setSelectedPermissions(
				getCollaboratorPermissions(t, record.permissions || []).map(
					(x) => x.value,
				),
			);
			setCheckAllPermissions(
				allPermissions.length == record?.permissions?.length,
			);
		},
		[editModalVisible],
	);
	const clearUpdateModal = useCallback(() => {
		setEditModalVisible(false);
		setEditModalContent(undefined);
		setCheckAllPermissions(false);
		setSelectedPermissions([]);
		setConfirmEditModalLoading(false);
	}, []);
	const resendInvitation = useCallback(
		(record: ProjectCollaboratorDto) =>
			handleSubmit([record.userEmail!!], record.permissions),
		[],
	);
	const [selectedPermissions, setSelectedPermissions] = useState<
		CheckboxValueType[]
	>([]);
	const [checkAllPermissions, setCheckAllPermissions] =
		useState<boolean>(false);
	const [confirmEditModalLoading, setConfirmEditModalLoading] = useState(false);
	const [inviteModalVisible, setInviteModalVisible] = useState(false);
	const inviteModalOpen = useCallback(() => {
		setInviteModalVisible(true);
	}, [inviteModalVisible]);
	const allPermissions = getAllCollaboratorPermissions(t);
	const showDeleteConfirmation = (
		collaboratorId: number,
		collaboratorName: string,
	) => deleteModal(collaboratorId, collaboratorName);
	const columns = useMemo(
		() =>
			getColumns(t, showDeleteConfirmation, editModalOpen, resendInvitation),
		[t, showDeleteConfirmation, editModalOpen, resendInvitation],
	);

	useEffect(() => {
		handleTableChange(pagination);
	}, []);

	const handleTableChange = async (tablePagination: TablePaginationConfig) => {
		setIsLoading(true);

		const result = await dispatch(
			getCollaborators({
				pageNum: tablePagination.current as number,
				pageSize: tablePagination.pageSize as number,
				projectId,
			}),
		);
		setIsLoading(false);

		if (getCollaborators.fulfilled.match(result)) {
			setProjectCollaborators(result.payload.items || []);
			setPagination({
				...pagination,
				...tablePagination,
				total: result.payload.totalCount || 0,
			});
		}
	};

	//delete Collaborator
	const deleteModal = (collaboratorId: number, collaboratorName: string) => {
		const deleteParams: HandleChangeParams = {
			collaboratorId,
			collaboratorName,
		};
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('collaboratorsPage.DeleteCollaboratorModal.Header')}
				</span>
			),
			okText: t('collaboratorsPage.DeleteCollaboratorModal.OkButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return checkProjectAdmin(deleteParams, handleDeleteCollaborator);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('collaboratorsPage.DeleteCollaboratorModal.Body', {
						collaboratorName,
					})}
				</span>
			),
		});
	};

	const handleDeleteCollaborator = async (data: HandleChangeParams) => {
		if (!data.collaboratorId) return;
		try {
			await dispatch(
				deleteCollaborator({ collaboratorId: data.collaboratorId, projectId }),
			);

			OmniNotification.success({
				message: t('collaboratorsPage.DeleteCollaboratorModal.Success.Header'),
				description: t(
					'collaboratorsPage.DeleteCollaboratorModal.Success.Body',
					{ collaboratorName: data.collaboratorName },
				),
			});
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} finally {
			await handleTableChange(pagination);
		}
	};
	//delete Collaborator

	//edit Collaborator
	const onPermissionChange = (list: CheckboxValueType[]) => {
		setSelectedPermissions(list);
		setCheckAllPermissions(list.length === allPermissions.length);
	};

	const onCheckAllPermissionsChange = (e: CheckboxChangeEvent) => {
		setSelectedPermissions(
			e.target.checked ? allPermissions.map((x) => x.value) : [],
		);
		setCheckAllPermissions(e.target.checked);
	};

	const handleUpdateCollaborator = async (data: HandleChangeParams) => {
		const updateCommand: UpdateCollaboratorsCommand = {
			collaboratorId: data.collaboratorId,
			projectId: projectId,
			permissions: data.permissions,
		};
		if (data.permissions && data.permissions.length <= 0) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('collaboratorsPage.NoPermissionsError'),
			});
			return;
		}
		const result = await dispatch(updateCollaborator(updateCommand));

		if (!updateCollaborator.fulfilled.match(result)) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} else {
			OmniNotification.success({
				message: t('collaboratorsPage.EditCollaboratorModal.Success.Header'),
				description: t('collaboratorsPage.EditCollaboratorModal.Success.Body', {
					collaboratorName:
						editModalContent?.userName || editModalContent?.userEmail,
				}),
			});
		}
		clearUpdateModal();
		await handleTableChange(pagination);
	};

	const checkPermissionChange = async () => {
		setConfirmEditModalLoading(true);
		const updateParams: HandleChangeParams = {
			collaboratorId: editModalContent?.collaboratorId,
			permissions: selectedPermissions.map((x) => x as number),
		};
		await checkProjectAdmin(updateParams, await handleUpdateCollaborator);
	};
	//edit Collaborator

	// reassign Project Administrator
	const checkProjectAdmin = (
		updateParams: HandleChangeParams,
		callback: Function,
	) => {
		if (!updateParams) return;

		const projectHasOtherAdmins = projectCollaborators
			?.filter((x) => x.collaboratorId !== updateParams.collaboratorId)
			.some((x) => x.isProjectAdministrator);
		const isAdmin = projectCollaborators?.find(
			(x) => x.collaboratorId === updateParams.collaboratorId,
		)?.isProjectAdministrator;

		if (projectHasOtherAdmins || !isAdmin) return callback(updateParams);
		else {
			clearUpdateModal();
			reassignAdminModal(updateParams, callback);
		}
	};

	const handleAddNewAdmin = async (selectedUser: number | undefined) => {
		const updateUserParams: HandleChangeParams = {
			collaboratorId: selectedUser,
			permissions: allPermissions.map((x) => x.value),
		};
		await handleUpdateCollaborator(updateUserParams);
	};

	const reassignAdminModal = (
		updateParams: HandleChangeParams,
		callback: Function,
	) => {
		let selectedUser: number | undefined = undefined;
		const reassignModal = Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('collaboratorsPage.ReassignAdminModal.Header')}
				</span>
			),
			okText: t('collaboratorsPage.ReassignAdminModal.OkButton'),
			cancelText: t('common.cancel'),
			async onOk() {
				await handleAddNewAdmin(selectedUser);
				return callback(updateParams);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			okButtonProps: { disabled: true },
			content: (
				<>
					<Trans
						i18nKey="collaboratorsPage.ReassignAdminModal.Body"
						components={[
							<a onClick={() => handleOpenInvitationModal(reassignModal)}></a>,
						]}
					/>
					<Select
						placeholder={'Select collaborator'}
						style={{ display: 'block' }}
						onChange={(value) => {
							selectedUser = value;
							reassignModal.update((prevConfig) => ({
								...prevConfig,
								okButtonProps: { disabled: !value },
							}));
						}}
						value={selectedUser}
					>
						{projectCollaborators &&
							projectCollaborators
								.filter(
									(value) =>
										value.collaboratorId !== updateParams.collaboratorId,
								)
								.map(
									(collaborator) =>
										!!collaborator.collaboratorId && (
											<Option value={collaborator.collaboratorId}>
												{!!collaborator.userName
													? collaborator.userName
													: collaborator.userEmail}
											</Option>
										),
								)}
					</Select>
				</>
			),
		});
	};
	// reassign Project Administrator

	//invite Collaborator
	function handleOpenInvitationModal(reassignModal?: { destroy: () => void }) {
		reassignModal?.destroy();
		inviteModalOpen();
	}

	const handleSubmit = async (
		invitationEmails: string[],
		permissions?: CollaboratorPermission[],
	) => {
		const successfulEmials: string[] = [];
		const failedEmails: string[] = [];

		for (const invitationEmail of invitationEmails) {
			const invitation: InviteCollaboratorCommand = {
				projectId: projectId,
				callbackUrl: `${window.location.origin}/invitation`,
				collaboratorEmail: invitationEmail,
				permissions: permissions,
			};
			try {
				await api.inviteProjectCollaborator(invitation);
				successfulEmials.push(invitationEmail);
			} catch (error) {
				failedEmails.push(invitationEmail);
			}
		}

		await handleTableChange(pagination);
		setInviteModalVisible(false);

		let notice: (args: ArgsProps) => void;
		if (successfulEmials.length == invitationEmails.length) {
			notice = OmniNotification.success;
		} else if (failedEmails.length == invitationEmails.length) {
			notice = OmniNotification.error;
		} else {
			notice = OmniNotification.info;
		}

		const successfulDescription =
			successfulEmials.length > 0
				? t('collaboratorsPage.InviteCollaboratorModal.Success.Description', {
						emails: invitationEmails
							.filter((email) => successfulEmials.includes(email))
							.join(', '),
					})
				: '';

		const failedDescription =
			failedEmails.length > 0
				? t('collaboratorsPage.InviteCollaboratorModal.Fail.Description', {
						emails: invitationEmails
							.filter((email) => failedEmails.includes(email))
							.join(', '),
					})
				: '';

		notice({
			message: t('collaboratorsPage.InviteCollaboratorModal.Success.Header'),
			description: `${successfulDescription}\n${failedDescription}`,
		});
	};

	//invite Collaborator

	return (
		<Layout.Content className={styles.pageContainer}>
			<div className="page-head">
				<div>
					<h1>{t('collaboratorsPage.Header')}</h1>
				</div>
				<div>
					<Button
						type="primary"
						data-testid="sendInvitationBtn"
						onClick={() => handleOpenInvitationModal()}
					>
						{t('collaboratorsPage.Header.InviteCollaborator')}
					</Button>
				</div>
			</div>
			<InviteModal
				visible={inviteModalVisible}
				setVisible={(visible) => setInviteModalVisible(visible)}
				onSubmit={handleSubmit}
				projectId={projectId}
				existingCollaborators={projectCollaborators}
			/>
			{editModalVisible && editModalContent && (
				<Modal
					title={
						<span>
							{t('collaboratorsPage.EditCollaboratorModal.Header', {
								collaboratorName: editModalContent.userName,
							})}
						</span>
					}
					visible
					centered
					okText={t('common.Save')}
					cancelText={t('common.cancel')}
					onOk={checkPermissionChange}
					onCancel={clearUpdateModal}
					cancelButtonProps={{
						className: 'color-dark',
					}}
					confirmLoading={confirmEditModalLoading}
					maskClosable={false}
					keyboard={false}
				>
					<div>
						<h2>{editModalContent.userName}</h2>
						<span data-testid="editUserEmail">
							{editModalContent.userEmail}
						</span>
					</div>
					<br />
					<div>
						<span>{t('collaboratorsPage.EditCollaboratorModal.Body')}</span>
					</div>
					<Space direction="vertical">
						<Checkbox
							defaultChecked={
								allPermissions.length == editModalContent?.permissions?.length
							}
							checked={checkAllPermissions}
							onChange={onCheckAllPermissionsChange}
						>
							{t('Collaborators.All')}
						</Checkbox>
						<Checkbox.Group
							defaultValue={editModalContent?.permissions || []}
							onChange={onPermissionChange}
							value={selectedPermissions}
						>
							<Space direction="vertical">
								{allPermissions.map((option) => (
									<Checkbox key={option.value} value={option.value}>
										{option.name}
									</Checkbox>
								))}
							</Space>
						</Checkbox.Group>
					</Space>
				</Modal>
			)}
			<Table
				className="table"
				size="middle"
				columns={columns}
				rowKey={(record) => record.collaboratorId || 0}
				dataSource={projectCollaborators}
				pagination={pagination}
				loading={isLoading}
				onChange={handleTableChange}
				tableLayout="fixed"
				data-testid="collaboratorsTable"
			/>
		</Layout.Content>
	);
}
